/**
 * Login page component
 * First screen users see when not authenticated
 */

import { type FormEvent, useCallback, useState } from "react";
import { useAuth } from "@/contexts";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import styles from "./LoginPage.module.css";

/**
 * Login form data
 */
interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

/**
 * Initial form state
 */
const initialFormData: LoginFormData = {
  email: "",
  password: "",
  rememberMe: false,
};

/**
 * Login page component
 */
export function LoginPage(): React.ReactElement {
  const { login, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Partial<LoginFormData>
  >({});

  /**
   * Validate form data
   */
  const validateForm = useCallback((): boolean => {
    const errors: Partial<LoginFormData> = {};

    if (!formData.email.trim()) {
      errors.email = "El email es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Introduce un email válido";
    }

    if (!formData.password) {
      errors.password = "La contraseña es obligatoria";
    } else if (formData.password.length < 6) {
      errors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      clearError();

      if (!validateForm()) {
        return;
      }

      try {
        await login({
          email: formData.email.trim(),
          password: formData.password,
          rememberMe: formData.rememberMe,
        });
      } catch {
        // Error is handled by AuthContext
      }
    },
    [formData, login, clearError, validateForm],
  );

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (field: keyof LoginFormData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value =
          event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value;

        setFormData((prev) => ({ ...prev, [field]: value }));

        // Clear field validation error on change
        if (validationErrors[field]) {
          setValidationErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        }
      },
    [validationErrors],
  );

  return (
    <div className={styles.container}>
      <div className={styles.loginWrapper}>
        {/* Logo and branding */}
        <div className={styles.branding}>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🐛</span>
            <h1 className={styles.logoText}>BioSAM</h1>
          </div>
          <p className={styles.tagline}>Gestión Profesional de Plagas</p>
        </div>

        {/* Login card */}
        <Card className={styles.loginCard}>
          <div className={styles.cardHeader}>
            <h2 className={styles.title}>Iniciar sesión</h2>
            <p className={styles.subtitle}>
              Introduce tus credenciales para acceder
            </p>
          </div>

          {/* Error alert */}
          {error && (
            <Alert
              variant="error"
              dismissible
              onDismiss={clearError}
              className={styles.alert}
            >
              {error}
            </Alert>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <Input
              type="email"
              label="Email"
              placeholder="tu@email.com"
              value={formData.email}
              onChange={handleInputChange("email")}
              error={validationErrors.email}
              autoComplete="email"
              autoFocus
              required
              disabled={isLoading}
              fullWidth
            />

            <Input
              type="password"
              label="Contraseña"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleInputChange("password")}
              error={validationErrors.password}
              autoComplete="current-password"
              required
              disabled={isLoading}
              fullWidth
            />

            <div className={styles.options}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleInputChange("rememberMe")}
                  disabled={isLoading}
                />
                <span>Recordarme</span>
              </label>
              <a href="#forgot" className={styles.forgotLink}>
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              isLoading={isLoading}
            >
              Iniciar sesión
            </Button>

            <div className={styles.quickLogin}>
              <p className={styles.quickLoginLabel}>Acceso rápido:</p>
              <div className={styles.quickLoginButtons}>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setFormData({
                      email: "admin@biosam.com",
                      password: "admin123",
                      rememberMe: false,
                    });
                    setValidationErrors({});
                  }}
                  disabled={isLoading}
                >
                  👨‍💼 Admin
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setFormData({
                      email: "cliente@hotelmeridian.com",
                      password: "cliente123",
                      rememberMe: false,
                    });
                    setValidationErrors({});
                  }}
                  disabled={isLoading}
                >
                  🏨 Cliente
                </Button>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className={styles.footer}>
            <p className={styles.footerText}>
              ¿No tienes cuenta?{" "}
              <a href="#register" className={styles.registerLink}>
                Contacta con el administrador
              </a>
            </p>
          </div>
        </Card>

        {/* Version info */}
        <p className={styles.version}>
          {import.meta.env.VITE_APP_NAME} v{import.meta.env.VITE_APP_VERSION}
        </p>
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className={styles.loadingOverlay} aria-hidden="true">
          <Spinner size="large" />
        </div>
      )}
    </div>
  );
}

export default LoginPage;
